import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ChainsService } from 'src/app/services/chains.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartType } from 'chart.js';
import {
  ReportFilterState,
  toApiParams,
  applyExportDetailParams,
  exportDetailFilenameSuffix,
  ExportDetailLevel,
  ExportRequestEvent,
} from 'src/app/shared/components/report-filter-bar/report-filter-bar.component';

@Component({
  selector: 'app-chain-reports',
  templateUrl: './chain-reports.component.html',
  styleUrls: ['./chain-reports.component.scss']
})
export class ChainReportsComponent implements OnInit {
  @ViewChild('trendChart') trendChart?: BaseChartDirective;

  chainId: string = '';
  chainName: string = '';
  role: string = localStorage.getItem('role') || '';

  filterState: ReportFilterState = { period: 'this_month' };
  chainStores: { _id: string; name: string }[] = [];

  totals: any = { totalRevenue: 0, totalOrders: 0, avgOrderValue: 0 };
  stores: any[] = [];
  sortedStores: any[] = [];
  sortDir: 'asc' | 'desc' = 'desc';

  dailyTrend: any[] = [];
  previousPeriodComparison: any = { available: false };
  returnsAndVoids: any = { returnsCount: 0, returnsAmount: 0, voidsCount: 0, voidsAmount: 0 };

  baBreakdown: any[] = [];
  expandedBas: Record<string, boolean> = {};
  expandedVaregrupper: Record<string, boolean> = {};
  expandedProducts: Record<string, boolean> = {};
  productSalesCache: Record<string, { loading: boolean; error: boolean; rows: any[]; pagination: any; currentPage: number }> = {};
  hasData: boolean = false;
  categoryLoading: boolean = false;
  categoryExportLoading: boolean = false;
  categoryExportDetailLevel: ExportDetailLevel = 'summary';
  reportsLoading: boolean = false;
  private reportsLoadSeq = 0;
  private categoryLoadSeq = 0;
  private readonly productSalesLimit = 20;
  private detectChangesPending = false;

  public trendChartData: ChartConfiguration['data'] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Revenue (DKK)',
        backgroundColor: 'rgba(51, 102, 255, 0.15)',
        borderColor: 'rgba(51, 102, 255, 1)',
        fill: true,
        yAxisID: 'y'
      },
      {
        data: [],
        label: 'Orders',
        borderColor: 'rgba(40, 167, 69, 1)',
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        fill: false,
        yAxisID: 'y1'
      }
    ]
  };

  public trendChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    interaction: { mode: 'index', intersect: false },
    scales: {
      y: { type: 'linear', display: true, position: 'left' },
      y1: { type: 'linear', display: true, position: 'right', grid: { drawOnChartArea: false } }
    },
    plugins: { legend: { display: true } }
  };

  public trendChartType: ChartType = 'line';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private chainsService: ChainsService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    this.route.params.subscribe(params => {
      this.chainId = params['id'];
    });
  }

  ngOnInit(): void {
    this.loadChainName();
    this.loadChainStores();
  }

  loadChainName(): void {
    this.chainsService.getChain(this.chainId).subscribe({
      next: (res: any) => {
        const chain = res.chain || res.response?.data?.chain || res.response?.data;
        this.chainName = chain?.chainName || '';
      },
      error: (err: any) => {
        this.toastr.error(err.error?.response?.message || 'Failed to load chain details');
      }
    });
  }

  loadChainStores(): void {
    this.chainsService.getChainStores(this.chainId).subscribe({
      next: (res: any) => {
        const raw = res.stores || res.response?.data?.stores || res.response?.data || res || [];
        const list = Array.isArray(raw) ? raw : [];
        this.chainStores = list.map((s: any) => ({
          _id: s._id || s.storeId,
          name: s.storeName || s.name || s._id
        }));
        if (!this.chainStores.length) {
          this.loadReports();
          this.loadCategoryReport();
        }
      },
      error: (err: any) => {
        this.toastr.error(err.error?.response?.message || 'Failed to load chain stores');
        this.loadReports();
        this.loadCategoryReport();
      }
    });
  }

  onFilterChange(filter: ReportFilterState): void {
    this.filterState = filter;
    this.loadReports();
    this.loadCategoryReport();
  }

  private apiParams(): Record<string, any> {
    return toApiParams(this.filterState);
  }

  loadReports(): void {
    const seq = ++this.reportsLoadSeq;
    this.reportsLoading = true;
    this.spinner.show();
    this.chainsService.getChainReports(this.chainId, this.apiParams()).subscribe({
      next: (res: any) => {
        if (seq !== this.reportsLoadSeq) return;
        try {
          const data = this.unwrapResponse(res);
          this.totals = data.totals || { totalRevenue: 0, totalOrders: 0, avgOrderValue: 0 };
          this.stores = data.stores || [];
          if (!this.chainStores.length && this.stores.length) {
            this.chainStores = this.stores.map((s: any) => ({
              _id: s.storeId || s._id,
              name: s.storeName || s.name
            }));
          }
          this.sortedStores = [...this.stores].sort((a, b) => b.totalRevenue - a.totalRevenue);
          this.dailyTrend = data.dailyTrend || [];
          this.previousPeriodComparison = data.previousPeriodComparison || data.comparison || { available: false };
          this.returnsAndVoids = data.returnsAndVoids || {
            returnsCount: 0, returnsAmount: 0, voidsCount: 0, voidsAmount: 0
          };
          this.hasData = this.stores.length > 0 || this.totals.totalOrders > 0;
          this.updateTrendChart();
        } finally {
          this.reportsLoading = false;
          this.spinner.hide();
        }
      },
      error: (err: any) => {
        if (seq !== this.reportsLoadSeq) return;
        this.reportsLoading = false;
        this.spinner.hide();
        Swal.fire('Error', err.error?.response?.message || 'Failed to load reports', 'error');
      }
    });
  }

  loadCategoryReport(): void {
    const seq = ++this.categoryLoadSeq;
    this.categoryLoading = true;
    this.expandedVaregrupper = {};
    this.expandedProducts = {};
    this.productSalesCache = {};
    this.chainsService.getChainCategoryReport(this.chainId, this.apiParams()).subscribe({
      next: (res: any) => {
        if (seq !== this.categoryLoadSeq) return;
        const data = this.unwrapResponse(res);
        this.baBreakdown = data?.baBreakdown || [];
        this.categoryLoading = false;
      },
      error: (err: any) => {
        if (seq !== this.categoryLoadSeq) return;
        this.baBreakdown = [];
        this.categoryLoading = false;
        this.toastr.error(err.error?.response?.message || 'Failed to load category breakdown');
      }
    });
  }

  onExport(event: ExportRequestEvent): void {
    this.spinner.show();
    const params = applyExportDetailParams({ format: event.format, ...this.apiParams() }, event.detailLevel);
    const suffix = exportDetailFilenameSuffix(event.detailLevel);
    this.chainsService.exportChainReports(this.chainId, params).subscribe({
      next: (blob: Blob) => {
        this.downloadBlob(
          blob,
          `chain-summary-${this.chainId}${suffix}-${new Date().toISOString().split('T')[0]}.${event.format}`
        );
        this.spinner.hide();
        this.toastr.success(this.translate.instant('KPI.CHAIN_REPORTS.EXPORT_SUCCESS'));
      },
      error: (err: any) => {
        this.spinner.hide();
        this.handleBlobError(err, 'Failed to export chain reports');
      }
    });
  }

  onCategoryExport(format: string): void {
    if (this.categoryExportLoading) return;
    this.categoryExportLoading = true;
    this.spinner.show();
    const params = applyExportDetailParams({ format, ...this.apiParams() }, this.categoryExportDetailLevel);
    const suffix = exportDetailFilenameSuffix(this.categoryExportDetailLevel);
    this.chainsService.exportChainCategoryReports(this.chainId, params).subscribe({
      next: (blob: Blob) => {
        this.downloadBlob(
          blob,
          `chain-product-breakdown-${this.chainId}${suffix}-${new Date().toISOString().split('T')[0]}.${format}`
        );
        this.spinner.hide();
        this.categoryExportLoading = false;
        this.toastr.success(this.translate.instant('KPI.CHAIN_REPORTS.EXPORT_PRODUCT_BREAKDOWN_SUCCESS'));
      },
      error: (err: any) => {
        this.spinner.hide();
        this.categoryExportLoading = false;
        this.handleBlobError(err, 'Failed to export product breakdown');
      }
    });
  }

  readonly exportFormats = ['csv', 'xlsx'];

  handleBlobError(error: any, fallback: string): void {
    if (error?.error instanceof Blob) {
      error.error.text().then((text: string) => {
        try {
          const parsed = JSON.parse(text);
          this.toastr.error(parsed?.response?.message || parsed?.message || fallback);
        } catch {
          this.toastr.error(text || fallback);
        }
      }).catch(() => this.toastr.error(fallback));
    } else {
      this.toastr.error(error?.error?.response?.message || error?.message || fallback);
    }
  }

  downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  toggleSort(): void {
    this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc';
    this.sortedStores = [...this.stores].sort((a, b) =>
      this.sortDir === 'desc'
        ? b.totalRevenue - a.totalRevenue
        : a.totalRevenue - b.totalRevenue
    );
  }

  private scheduleDetectChanges(): void {
    if (this.detectChangesPending) {
      return;
    }
    this.detectChangesPending = true;
    setTimeout(() => {
      this.detectChangesPending = false;
      this.cdr.detectChanges();
    });
  }

  toggleBa(baId: string): void {
    this.expandedBas = { ...this.expandedBas, [baId]: !this.expandedBas[baId] };
    this.scheduleDetectChanges();
  }

  isExpanded(baId: string): boolean {
    return !!this.expandedBas[baId];
  }

  toggleVg(baId: string, categoryId: string): void {
    const key = `${baId}_${categoryId}`;
    this.expandedVaregrupper = { ...this.expandedVaregrupper, [key]: !this.expandedVaregrupper[key] };
    this.scheduleDetectChanges();
  }

  isVgExpanded(baId: string, categoryId: string): boolean {
    return !!this.expandedVaregrupper[`${baId}_${categoryId}`];
  }

  productKey(baId: string, categoryId: string, productId: string): string {
    return `${baId}_${categoryId}_${productId}`;
  }

  toggleProduct(baId: string, categoryId: string, productId: string): void {
    const key = this.productKey(baId, categoryId, productId);
    const willExpand = !this.expandedProducts[key];
    this.expandedProducts = { ...this.expandedProducts, [key]: willExpand };
    if (willExpand && !this.productSalesCache[key]) {
      this.loadProductSales(baId, categoryId, productId, 1);
    }
    this.scheduleDetectChanges();
  }

  isProductExpanded(baId: string, categoryId: string, productId: string): boolean {
    return !!this.expandedProducts[this.productKey(baId, categoryId, productId)];
  }

  loadProductSales(baId: string, categoryId: string, productId: string, page: number = 1): void {
    const key = this.productKey(baId, categoryId, productId);
    const isLoadMore = page > 1;

    if (!isLoadMore) {
      this.productSalesCache = {
        ...this.productSalesCache,
        [key]: { loading: true, error: false, rows: [], pagination: null, currentPage: 1 }
      };
    } else {
      const existing = this.productSalesCache[key];
      if (!existing || existing.loading) return;
      this.productSalesCache = {
        ...this.productSalesCache,
        [key]: { ...existing, loading: true }
      };
    }
    this.scheduleDetectChanges();

    const params = {
      ...this.apiParams(),
      page,
      limit: this.productSalesLimit
    };

    this.chainsService.getChainProductSaleEvents(this.chainId, productId, params).subscribe({
      next: (res: any) => {
        const data = this.unwrapResponse(res);
        const prev = this.productSalesCache[key];
        this.productSalesCache = {
          ...this.productSalesCache,
          [key]: {
            loading: false,
            error: false,
            rows: isLoadMore ? [...(prev?.rows || []), ...(data.sales || [])] : (data.sales || []),
            pagination: data.pagination,
            currentPage: data.pagination?.currentPage ?? page
          }
        };
        this.scheduleDetectChanges();
      },
      error: (err: any) => {
        if (!isLoadMore) {
          this.productSalesCache = {
            ...this.productSalesCache,
            [key]: { loading: false, error: true, rows: [], pagination: null, currentPage: 1 }
          };
        } else {
          const existing = this.productSalesCache[key];
          this.productSalesCache = {
            ...this.productSalesCache,
            [key]: { ...existing, loading: false }
          };
        }
        this.scheduleDetectChanges();
        this.toastr.error(err.error?.response?.message || this.translate.instant('KPI.ANALYTICS.LOAD_SALES_ERROR'));
      }
    });
  }

  loadMoreProductSales(baId: string, categoryId: string, productId: string): void {
    const key = this.productKey(baId, categoryId, productId);
    const cache = this.productSalesCache[key];
    if (!cache || cache.loading) return;
    if (!cache.pagination || cache.currentPage >= cache.pagination.totalPages) return;
    this.loadProductSales(baId, categoryId, productId, cache.currentPage + 1);
  }

  formatOrderDate(orderDate: string): string {
    if (!orderDate) return '-';
    return new Date(orderDate).toLocaleDateString('da-DK');
  }

  formatOrderTime(orderDate: string): string {
    if (!orderDate) return '-';
    return new Date(orderDate).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
  }

  updateTrendChart(): void {
    this.trendChartData.labels = this.dailyTrend.map(d => d.date);
    this.trendChartData.datasets[0].data = this.dailyTrend.map(d => d.revenue || 0);
    this.trendChartData.datasets[1].data = this.dailyTrend.map(d => d.orders || 0);
    const chart = this.trendChart;
    if (chart && typeof chart.update === 'function') {
      chart.update();
    }
  }

  unwrapResponse(res: any): any {
    if (res?.response?.success && res.response.data !== undefined) return res.response.data;
    if (res?.code === 200 && res.data !== undefined) return res.data;
    return res;
  }

  formatCurrency(value: number): string {
    if (value == null || isNaN(value)) {
      return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK' }).format(0);
    }
    return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK' }).format(value);
  }

  formatNullableCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) return '-';
    return this.formatCurrency(value);
  }

  formatMoney(value: number | null | undefined, nullable = false): string {
    if (nullable && (value === null || value === undefined)) return '-';
    return this.formatCurrency(value ?? 0);
  }

  formatTaxRate(value: number | null | undefined): string {
    if (value === null || value === undefined || isNaN(value)) return '-';
    return `${value.toFixed(2)}%`;
  }

  formatNumber(value: number): string {
    if (value == null) return '0';
    return new Intl.NumberFormat('da-DK').format(value);
  }

  formatDelta(deltaPct: number | null | undefined): string {
    if (deltaPct == null || isNaN(deltaPct)) return '';
    const sign = deltaPct >= 0 ? '+' : '';
    return `${sign}${deltaPct.toFixed(1)}%`;
  }

  deltaClass(deltaPct: number | null | undefined): string {
    if (deltaPct == null || isNaN(deltaPct)) return 'text-muted';
    if (deltaPct > 0) return 'text-success';
    if (deltaPct < 0) return 'text-danger';
    return 'text-muted';
  }

  get backLabel(): string {
    return this.role === 'chain_manager'
      ? this.translate.instant('KPI.CHAIN_REPORTS.BACK_MY_CHAINS')
      : this.translate.instant('KPI.CHAIN_REPORTS.BACK_CHAINS');
  }

  goBack(): void {
    if (this.role === 'chain_manager') {
      this.router.navigate(['/my-chains']);
    } else {
      this.router.navigate(['/chains']);
    }
  }
}
